/**
 * A retail user's request to delete their account (MAD-5179, madkhol-user `account_deletion_request`).
 *
 * SUBMITTED for a 48-hour review (the account is frozen) → Support may CANCEL it; once the review is over, Ops STARTS
 * it (PROCESSING: it can no longer be cancelled), runs the delete-user runbook, then marks it COMPLETED.
 */
export enum AccountDeletionStatus {
  SUBMITTED = 'SUBMITTED',
  CANCELLED = 'CANCELLED',
  PROCESSING = 'PROCESSING',
  COMPLETED = 'COMPLETED',
}

export interface AccountDeletionRequest {
  id: number;
  /** Kept after the purge, when the user row no longer exists. */
  userId: number;
  /** The reason the user picked in the app, as a stable code (e.g. `not_useful`). */
  reasonCode: string;
  status: AccountDeletionStatus;
  /** The app language at submission (`ar` | `en`): the notices go out in it. */
  language: string;
  /** A snapshot taken at submission for the completion SMS; cleared once that is sent. */
  contactMobile: string | null;
  contactEmail: string | null;
  createdAt: string;
  /** When the review ends and Ops may start the deletion. */
  reviewEndsAt: string;
  cancelledAt: string | null;
  cancelledBy: number | null;
  startedAt: string | null;
  startedBy: number | null;
  completedAt: string | null;
  completedBy: number | null;
}

/** Each flag is true when the user still has that activity, i.e. it must be cleared before the deletion starts. */
export interface AccountDeletionBlockers {
  activeInvestments: boolean;
  cashBalance: boolean;
  pendingTransactions: boolean;
  ratbiPlus: boolean;
}

export interface AccountDeletionEligibility {
  canDelete: boolean;
  blockers: AccountDeletionBlockers;
}

/** The queue filters madkhol-user accepts; it refuses any other query parameter. */
export interface FetchAccountDeletionRequestsArgs {
  status?: AccountDeletionStatus;
  /** SUBMITTED and past the review: ready to start. */
  readyOnly?: boolean;
  offset: number;
  limit: number;
}

export interface FetchAccountDeletionRequestsResponse {
  items: AccountDeletionRequest[];
  totalItems: number;
}
